import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { classMap } from 'lit/directives/class-map.js'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { style } from './price.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { PdpSelectedTierContext, type PdpSelectedTierContextType } from 'components/discovery/pdp/provider.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import { InternalProduct, ProductModel } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'


export const priceSizes = ['small', 'medium', 'large'] as const
export type priceSizeTypes = typeof priceSizes

export const vatModes = ['incl', 'excl'] as const
export type VatMode = typeof vatModes[number]

export const priceHiddenFields = ['price1', 'price2', 'uom', 'vat'] as const
export type PriceHiddenField = typeof priceHiddenFields[number]

export const discountPlacements = ['left', 'top'] as const
export type discountPlacementTypes = typeof discountPlacements

export const uomPlacements = ['right', 'bottom'] as const
export type uomPlacementTypes = typeof uomPlacements

export const priceDataSources = ['attributes', 'pdp-product'] as const
export type PriceDataSource = typeof priceDataSources[number]

export const priceVariants = ['default', 'sale'] as const
export type priceVariantTypes = typeof priceVariants

export const priceLayouts = [
  'inline',
  'inline-vat-below',
  'lead-inline',
  'lead-inline-vat-below',
  'inline-meta-below',
  'stacked',
] as const
export type priceLayoutTypes = typeof priceLayouts

/** Which product price to display when csc-price is used as a product tile widget */
export type PriceSource = 'sale' | 'consumer' | 'customer' | 'list' | 'sale-taxed' | 'sale-display' | 'per-price-uom'

/** Size vocabulary of the per-price tile widget properties (price1-size / price2-size) */
export const tilePriceSizes = ['l', 'm', 's'] as const
export type TilePriceSize = (typeof tilePriceSizes)[number]

interface PriceLineConfig {
  finalSize: 's' | 'm' | 'l'
  variant: priceVariantTypes[number]
  layout: priceLayoutTypes[number] | ''
  lead: string
  uom: string
  uomPlacement: uomPlacementTypes[number]
  discountPlacement: discountPlacementTypes[number]
  salePrice: number
  salePriceFormatted: string
  salePriceTaxed: number
  consumerPrice: number
  customerPriceFormatted: string
  useCustomerPrice: boolean
  useConsumerPrice: boolean
  displayAsDiscount: boolean
  discountPercentage: string
  showVat: boolean
  vatMode: VatMode | ''
  currencyCode: string
  locale: string
}

const emptyTileProduct: InternalProduct = new ProductModel().parseToInternal()

/** Tile product with the prices and unit of a PDP product, the only parts tile mode reads */
function mapPdpProductPrices(product: PdpProduct): InternalProduct {
  return {
    ...emptyTileProduct,
    id: product.id,
    name: product.name,
    code: product.code,
    prices: {
      ...emptyTileProduct.prices,
      consumer: product.prices.consumer,
      currencyCode: product.prices.currency_code ?? '',
      customer: product.prices.customer,
      sale: product.prices.sale ?? '',
      saleTaxed: product.prices.sale_taxed ?? '',
      list: product.prices.list ?? '',
      pricePerPriceUom: product.prices.price_per_price_uom || '',
      priceUom: product.prices.price_uom ?? '',
    },
    ordering: {
      ...emptyTileProduct.ordering,
      defaultUom: product.ordering.default_uom,
    },
  }
}

@customElement('csc-price')
export class CSCPrice extends HiddenFieldsMixin(LitElement, priceHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Number, attribute: 'sale-price' })
  salePrice: number = 0

  @property({ type: String, attribute: 'sale-price-formatted' })
  salePriceFormatted!: string

  @property({ type: Number, attribute: 'sale-price-taxed' })
  salePriceTaxed: number = 0

  @property({ type: Number, attribute: 'consumer-price' })
  consumerPrice: number = 0

  @property({ type: String, attribute: 'customer-price-formatted' })
  customerPriceFormatted: string = ''

  @property({ type: Boolean, attribute: 'use-customer-price' })
  useCustomerPrice: boolean = true

  @property({ type: Boolean, attribute: 'use-consumer-price' })
  useConsumerPrice: boolean = false

  @property({ type: Boolean, attribute: 'display-as-discount' })
  displayAsDiscount: boolean = false

  @property({ type: String, attribute: 'discount-percentage' })
  discountPercentage: string = ''

  @property({ type: String })
  size: priceSizeTypes[number] = 'medium'

  @property({ type: String, attribute: 'price-variant' })
  priceVariant: priceVariantTypes[number] = 'default'

  /** @deprecated Use `price-variant` instead. */
  @property({ type: String })
  set variant(value: priceVariantTypes[number]) {
    console.warn('<csc-price> "variant" is deprecated, use "price-variant" instead.')
    this.priceVariant = value
  }

  get variant() {
    return this.priceVariant
  }

  @property({ type: String })
  layout: priceLayoutTypes[number] | '' = ''

  @property({ type: String })
  lead: string = ''

  @property({ type: String, attribute: 'discount-placement' })
  discountPlacement: discountPlacementTypes[number] = 'left'

  @property({ type: String, attribute: 'uom-placement' })
  uomPlacement: uomPlacementTypes[number] = 'right'

  @property({ type: String })
  uom: string = ''

  /** @deprecated Use `hidden-fields="uom"` instead. */
  @property({ type: Boolean, attribute: 'hide-uom' })
  set hideUom(value: boolean) {
    console.warn('<csc-price> "hide-uom" is deprecated, use hidden-fields="uom" instead.')
    this.setLegacyHidden('uom', value)
  }

  get hideUom() {
    return this.isPartHidden('uom')
  }

  @property({ type: String, attribute: 'currency-code' })
  currencyCode: string = 'USD'

  @property({ type: String })
  locale = 'en-US'

  /** @deprecated Use `hidden-fields="vat"` instead. */
  @property({ type: Boolean, attribute: 'show-vat' })
  set showVat(value: boolean) {
    console.warn('<csc-price> "show-vat" is deprecated, use hidden-fields="vat" instead.')
    this.setLegacyHidden('vat', !value)
  }

  get showVat() {
    return !this.isPartHidden('vat')
  }

  @property({ type: String, attribute: 'vat-mode' })
  vatMode: VatMode | '' = ''

  /** @deprecated Use `vat-mode` instead. */
  @property({ type: String, attribute: 'vat-display' })
  set vatDisplay(value: VatMode | '') {
    console.warn('<csc-price> "vat-display" is deprecated, use "vat-mode" instead.')
    this.vatMode = value
  }

  get vatDisplay() {
    return this.vatMode
  }

  // --- Product tile widget mode ---
  // When csc-price is rendered inside a csc-base-product-tile, it consumes the tile's
  // product context instead of the properties above, and can show up to two prices.
  // With `data-source="pdp-product"` it does the same with the product loaded by csc-pdp-provider.

  /** Where the prices come from: the attributes above, or the product loaded by csc-pdp-provider (shown with the price1/price2 properties) */
  @property({ type: String, attribute: 'data-source' })
  dataSource: PriceDataSource = 'attributes'

  @property({ type: Boolean, attribute: 'use-csc-price' })
  useCscPrice = false

  /** @deprecated Use `hidden-fields="price1"` instead. */
  @property({ type: Boolean, attribute: 'show-price1' })
  set showPrice1(value: boolean) {
    console.warn('<csc-price> "show-price1" is deprecated, use hidden-fields="price1" instead.')
    this.setLegacyHidden('price1', !value)
  }

  get showPrice1() {
    return !this.isPartHidden('price1')
  }

  @property({ type: String, attribute: 'price1-source' })
  price1Source: PriceSource = 'sale'

  @property({ type: String, attribute: 'price1-variant' })
  price1Variant: priceVariantTypes[number] = 'default'

  @property({ type: String, attribute: 'price1-size' })
  price1Size: TilePriceSize = 'm'

  @property({ type: String, attribute: 'price1-layout' })
  price1Layout: priceLayoutTypes[number] = 'inline'

  @property({ type: String, attribute: 'price1-lead' })
  price1Lead = ''

  @property({ type: String, attribute: 'price1-uom' })
  price1Uom = ''

  @property({ type: String, attribute: 'price1-vat' })
  price1Vat = ''

  @property({ type: Boolean, attribute: 'price1-show-vat' })
  price1ShowVat = false

  @property({ type: String, attribute: 'price1-vat-mode' })
  price1VatMode: VatMode | '' = ''

  /** @deprecated Use `price1-vat-mode` instead. */
  @property({ type: String, attribute: 'price1-vat-display' })
  set price1VatDisplay(value: VatMode | '') {
    console.warn('<csc-price> "price1-vat-display" is deprecated, use "price1-vat-mode" instead.')
    this.price1VatMode = value
  }

  get price1VatDisplay() {
    return this.price1VatMode
  }

  /** @deprecated Set `price2-source` to show the second price, `hidden-fields="price2"` to hide it. */
  @property({ type: Boolean, attribute: 'show-price2' })
  set showPrice2(value: boolean) {
    console.warn('<csc-price> "show-price2" is deprecated, set "price2-source" to show the second price or use hidden-fields="price2" to hide it.')
    if (value && !this.price2Source) this.price2Source = 'consumer'
    this.setLegacyHidden('price2', !value)
  }

  get showPrice2() {
    return this.showsPrice2
  }

  @property({ type: String, attribute: 'price2-source' })
  price2Source: PriceSource | '' = ''

  @property({ type: String, attribute: 'price2-variant' })
  price2Variant: priceVariantTypes[number] = 'default'

  @property({ type: String, attribute: 'price2-size' })
  price2Size: TilePriceSize = 's'

  @property({ type: String, attribute: 'price2-layout' })
  price2Layout: priceLayoutTypes[number] = 'inline'

  @property({ type: String, attribute: 'price2-lead' })
  price2Lead = ''

  @property({ type: String, attribute: 'price2-uom' })
  price2Uom = ''

  @property({ type: String, attribute: 'price2-vat' })
  price2Vat = ''

  @property({ type: Boolean, attribute: 'price2-show-vat' })
  price2ShowVat = false

  @property({ type: String, attribute: 'price2-vat-mode' })
  price2VatMode: VatMode | '' = ''

  /** @deprecated Use `price2-vat-mode` instead. */
  @property({ type: String, attribute: 'price2-vat-display' })
  set price2VatDisplay(value: VatMode | '') {
    console.warn('<csc-price> "price2-vat-display" is deprecated, use "price2-vat-mode" instead.')
    this.price2VatMode = value
  }

  get price2VatDisplay() {
    return this.price2VatMode
  }

  private legacyHiddenFields = new Set<PriceHiddenField>()

  @consume({ context: PdpSelectedTierContext, subscribe: true })
  @state()
  private selectedTier: PdpSelectedTierContextType = null

  @consume({ context: productTileProductContext })
  @state()
  private tileProduct: InternalProduct = emptyTileProduct

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @state()
  private pdpProduct: InternalProduct = emptyTileProduct

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) this.pdpProduct = mapPdpProductPrices(product)
    })
  }

  private setLegacyHidden(field: PriceHiddenField, hidden: boolean) {
    if (hidden) this.legacyHiddenFields.add(field)
    else this.legacyHiddenFields.delete(field)
    this.requestUpdate()
  }

  private isPartHidden(field: PriceHiddenField): boolean {
    return this.isHidden(field) || this.legacyHiddenFields.has(field)
  }

  private get showsPrice2(): boolean {
    return !!this.price2Source && !this.isPartHidden('price2')
  }

  /** True when an ancestor csc-base-product-tile provides real product data */
  private get isInTile(): boolean {
    return this.tileProduct !== emptyTileProduct
  }

  /** The product whose prices are shown in tile mode: the tile's product, or the PDP product with data-source="pdp-product" */
  private get sourceProduct(): InternalProduct {
    return this.isInTile ? this.tileProduct : this.pdpProduct
  }

  private mapSize(size: priceSizeTypes[number]): 's' | 'm' | 'l' {
    const map = { small: 's' as const, medium: 'm' as const, large: 'l' as const }
    return map[size] ?? 'm'
  }

  private fromPriceOf(cfg: PriceLineConfig) {
    if (cfg.useCustomerPrice && cfg.customerPriceFormatted) {
      return cfg.customerPriceFormatted
    }
    if (cfg.useConsumerPrice && cfg.consumerPrice > 0) {
      return this.displayPriceWithCurrency(cfg.consumerPrice, cfg.currencyCode, cfg.locale)
    }
    return ''
  }

  private resolvedLayoutOf(cfg: PriceLineConfig): priceLayoutTypes[number] {
    if (cfg.layout) return cfg.layout
    return cfg.uomPlacement === 'bottom' ? 'inline-meta-below' : 'inline'
  }

  private vatLabelOf(cfg: PriceLineConfig) {
    if (!cfg.showVat) return ''
    if (cfg.vatMode === 'incl') return t('tax_included_suffix', { ns: 'csc-price', default: 'incl. VAT' })
    if (cfg.vatMode === 'excl') return t('tax_excluded_suffix', { ns: 'csc-price', default: 'excl. VAT' })
    return cfg.salePrice === cfg.salePriceTaxed
      ? t('tax_included_suffix', { ns: 'csc-price', default: 'incl. VAT' })
      : t('tax_excluded_suffix', { ns: 'csc-price', default: 'excl. VAT' })
  }

  private discountLeadOf(cfg: PriceLineConfig) {
    if (this.selectedTier?.discount) {
      return this.displayPriceWithCurrency(cfg.salePrice, cfg.currencyCode, cfg.locale)
    }
    return this.fromPriceOf(cfg) || cfg.lead
  }

  private activeVariantOf(cfg: PriceLineConfig): priceVariantTypes[number] {
    if (cfg.displayAsDiscount || !!this.fromPriceOf(cfg) || !!this.selectedTier?.discount) return 'sale'
    return cfg.variant
  }

  private activePriceOf(cfg: PriceLineConfig): string | number {
    if (this.selectedTier) {
      return this.selectedTier.price
    }
    return cfg.salePriceFormatted && cfg.salePriceFormatted.trim() !== ''
      ? cfg.salePriceFormatted
      : this.displayPriceWithCurrency(cfg.salePrice, cfg.currencyCode, cfg.locale)
  }

  private displayPriceWithCurrency(price: number, currencyCode: string, locale: string) {
    if (!currencyCode) return price

    try {
      return Intl.NumberFormat(
        locale, { style: 'currency', currency: currencyCode }
      ).format(Number(price))
    } catch (error) {
      console && console.error(error)
      return price
    }
  }

  private renderPriceLine(cfg: PriceLineConfig) {
    return html`
      <div class="${classMap({
        'csc-price': true,
        'csc-price--placement-left': cfg.discountPlacement === 'left',
      })}">
        ${cfg.discountPercentage
          ? html`<cse-text class="percentage" variant="caption-m">${cfg.discountPercentage}</cse-text>`
          : nothing
        }
        <cse-price
          size="${cfg.finalSize}"
          variant="${this.activeVariantOf(cfg)}"
          layout="${this.resolvedLayoutOf(cfg)}"
          lead="${this.discountLeadOf(cfg)}"
          uom="${cfg.uom}"
          vat="${this.vatLabelOf(cfg)}"
        >
          ${this.activePriceOf(cfg)}
        </cse-price>
      </div>
    `
  }

  private get standaloneLineConfig(): PriceLineConfig {
    return {
      finalSize: this.mapSize(this.size),
      variant: this.priceVariant,
      layout: this.layout,
      lead: this.lead,
      uom: this.isPartHidden('uom') ? '' : this.uom,
      uomPlacement: this.uomPlacement,
      discountPlacement: this.discountPlacement,
      salePrice: this.salePrice,
      salePriceFormatted: this.salePriceFormatted,
      salePriceTaxed: this.salePriceTaxed,
      consumerPrice: this.consumerPrice,
      customerPriceFormatted: this.customerPriceFormatted,
      useCustomerPrice: this.useCustomerPrice,
      useConsumerPrice: this.useConsumerPrice,
      displayAsDiscount: this.displayAsDiscount,
      discountPercentage: this.discountPercentage,
      showVat: !this.isPartHidden('vat'),
      vatMode: this.vatMode,
      currencyCode: this.currencyCode,
      locale: this.locale,
    }
  }

  private getTilePriceValue(source: PriceSource): string | number | null {
    switch (source) {
      case 'sale':
        return this.sourceProduct.prices.sale ?? null
      case 'consumer':
        return this.sourceProduct.prices.consumer ?? null
      case 'customer':
        return this.sourceProduct.prices.customer ?? null
      case 'list':
        return this.sourceProduct.prices.list ?? null
      case 'sale-taxed':
        return this.sourceProduct.prices.saleTaxed ?? null
      case 'sale-display':
        return this.sourceProduct.prices.saleDisplay ?? null
      case 'per-price-uom':
        return this.showsPerPriceUom(source) ? this.sourceProduct.prices.pricePerPriceUom : (this.sourceProduct.prices.sale ?? null)
      default:
        return this.sourceProduct.prices.sale ?? null
    }
  }

  private showsPerPriceUom(source: PriceSource): boolean {
    const { pricePerPriceUom, sale } = this.sourceProduct.prices
    return source === 'per-price-uom' && !!pricePerPriceUom && sale != null && sale !== ''
  }

  private tilePriceUom(source: PriceSource, uom: string, fallbackUom: string): string {
    if (this.isPartHidden('uom')) return ''
    if (uom) return uom
    return this.showsPerPriceUom(source) ? (this.sourceProduct.prices.priceUom || fallbackUom) : fallbackUom
  }

  private formatTilePrice(value: string | number | null): string {
    if (value == null || value === '') return ''
    const num = Number(value)
    if (Number.isNaN(num)) return String(value)
    try {
      return Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.sourceProduct.prices.currencyCode || 'EUR',
      }).format(num)
    } catch {
      return String(value)
    }
  }

  private renderTilePriceSlot(
    show: boolean,
    source: PriceSource,
    variant: priceVariantTypes[number],
    size: TilePriceSize,
    layout: priceLayoutTypes[number],
    lead: string,
    uom: string,
    vat: string,
    showVat: boolean,
    showDiscount: boolean,
    vatMode: VatMode | '',
  ) {
    if (!show) return nothing

    const showsPerPriceUom = this.showsPerPriceUom(source)
    const showsDiscount = showDiscount && !showsPerPriceUom

    if (this.useCscPrice) {
      const prices = this.sourceProduct.prices
      const sourceValue = this.getTilePriceValue(source)
      const rawSale = sourceValue == null || sourceValue === '' ? prices.sale : sourceValue
      if (rawSale == null || rawSale === '') {
        return nothing
      }
      const saleIsFormatted = Number.isNaN(Number(rawSale))
      const salePrice = saleIsFormatted ? Number(prices.saleNoFormat ?? 0) : Number(rawSale)
      const salePriceFormatted = saleIsFormatted ? String(rawSale ?? '') : ''

      return this.renderPriceLine({
        finalSize: size,
        variant,
        layout,
        lead,
        uom,
        uomPlacement: 'bottom',
        discountPlacement: 'left',
        salePrice,
        salePriceFormatted,
        salePriceTaxed: Number(prices.saleTaxed ?? 0),
        consumerPrice: Number(prices.consumer ?? 0),
        customerPriceFormatted: showsDiscount ? String(prices.customer ?? '') : '',
        useCustomerPrice: true,
        useConsumerPrice: false,
        displayAsDiscount: showsDiscount && !!prices.displayAsDiscount,
        discountPercentage: '',
        showVat,
        vatMode: showsPerPriceUom ? 'excl' : vatMode,
        currencyCode: prices.currencyCode,
        locale: this.locale,
      })
    }

    const raw = this.getTilePriceValue(source)
    if (raw == null || raw === '') return nothing
    const formatted = this.formatTilePrice(raw)
    return html`
      <cse-price
        variant="${variant}"
        size="${size}"
        layout="${layout}"
        lead="${lead}"
        uom="${uom}"
        vat="${vat}"
      >${formatted}</cse-price>
    `
  }

  private renderTileMode() {
    const showsPrice1 = !this.isPartHidden('price1')
    if (!showsPrice1 && !this.showsPrice2) return nothing
    const price1ShowsDiscount = showsPrice1 && this.useCscPrice && !this.showsPerPriceUom(this.price1Source)
      && (Number(this.sourceProduct.prices.consumer) > 0 || !!this.sourceProduct.prices.customer || this.sourceProduct.prices.displayAsDiscount)

    return html`
      <div class="csc-product-tile-prices">
        ${showsPrice1
          ? this.renderTilePriceSlot(
              true,
              this.price1Source,
              this.price1Variant,
              this.price1Size,
              this.price1Layout,
              this.price1Lead,
              this.tilePriceUom(this.price1Source, this.price1Uom, this.sourceProduct.ordering.defaultUom),
              this.price1Vat,
              this.price1ShowVat,
              true,
              this.price1VatMode,
            )
          : nothing}
        ${this.showsPrice2 && this.price2Source
          ? this.renderTilePriceSlot(
              true,
              this.price2Source,
              this.price2Variant,
              this.price2Size,
              this.price2Layout,
              this.price2Lead,
              this.tilePriceUom(this.price2Source, this.price2Uom, ''),
              this.price2Vat,
              this.price2ShowVat,
              !price1ShowsDiscount,
              this.price2VatMode,
            )
          : nothing}
      </div>
    `
  }

  override render() {
    if (this.isInTile) {
      return this.renderTileMode()
    }
    if (this.dataSource === 'pdp-product') {
      return this.pdpProduct === emptyTileProduct ? nothing : this.renderTileMode()
    }
    return this.renderPriceLine(this.standaloneLineConfig)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-price': CSCPrice
  }
}
